import { Tag } from 'antd';
import { useDebtsReportQuery, useLazyExportDebtsXlsxQuery } from '@/entities/reports';
import type { DebtsReportRow } from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import type { ResponsiveColumns } from '@/shared/ui';
import { formatMoney, sumMoney } from '@/shared/lib/money';

const STATUS_COLOR: Record<DebtsReportRow['status'], string> = { YOU_OWE_US: 'success', WE_OWE_THEM: 'error' };

/** §13, §7: контрагенты с ненулевым балансом. */
export function DebtsReportPage() {
  const { data, isFetching } = useDebtsReportQuery(undefined, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportDebtsXlsxQuery();

  const columns: ResponsiveColumns<DebtsReportRow> = [
    { title: 'Контрагент', dataIndex: 'counterpartyName' },
    {
      title: 'Кто должен',
      dataIndex: 'status',
      width: 140,
      mobile: 'badge',
      render: (_v, row) => <Tag color={STATUS_COLOR[row.status]}>{row.statusLabel}</Tag>,
    },
    // Сумма без знака: «Мы должны 9270.00», а не «Мы должны −9270.00».
    { title: 'Сумма', dataIndex: 'amount', width: 120, align: 'right', render: (v: string) => formatMoney(v) },
    { title: 'Неоплаченных документов', dataIndex: 'unpaidDocumentsCount', width: 170, align: 'right' },
  ];

  const rows = data?.rows ?? [];
  // Раздельные итоги: «нам должны» и «мы должны» складывать нельзя — у них разный смысл.
  const owedToUs = sumMoney(rows.filter((r) => r.status === 'YOU_OWE_US').map((r) => r.amount));
  const weOwe = sumMoney(rows.filter((r) => r.status === 'WE_OWE_THEM').map((r) => r.amount));
  const totals =
    rows.length > 0
      ? {
          cells: {
            amount: (
              <>
                <div>Нам должны: {formatMoney(owedToUs)}</div>
                <div>Мы должны: {formatMoney(weOwe)}</div>
              </>
            ),
          },
        }
      : undefined;

  return (
    <ReportPage
      title="Долги"
      columns={columns}
      data={rows}
      totals={totals}
      loading={isFetching}
      rowKey="counterpartyId"
      onExport={() => exportXlsx().unwrap()}
      exportFilename="Долги.xlsx"
    />
  );
}
